import { useQuery } from '@tanstack/react-query'
import { UniDocument } from '@vendia/control-plane-api'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import useControlPlaneApi from './use-control-plane-api'
import safeNotify from '../security/safe-notify'
import { useSafeParams } from '../security/use-safe-params'
import { selectedRoleState } from '../state'
import { StatusEnum } from '@vendia/management-api-types'

export type GetUni = NonNullable<ReturnType<typeof useGetUni>['data']>['getUni']
export type GetUniNodes = NonNullable<GetUni['nodes']>

export const useGetUni = ({ polling = false }: { polling?: boolean } = {}) => {
  const client = useControlPlaneApi()
  const navigate = useNavigate()
  const { id: uniName } = useSafeParams()
  const selectedRoleName = useRecoilValue(selectedRoleState).name

  const response = useQuery({
    queryKey:
      // Adding selected role to query key forces the UI to refresh on role switch and kicks users back to dashboard if they're
      // on any page nested within the /uni/my-uni-name path (e.g. /uni/my-uni-name, /uni/my-uni-name/nodeone/details, etc.)
      ['getUni', selectedRoleName, uniName],
    queryFn: async () => {
      const data = await client.graphqlFetch({
        query: UniDocument,
        variables: { uniId: uniName! },
      })
      if (data.errors) {
        // Users typically hit a uni "not found" error if they're in a stale tab (logged into another account since) or "unathorized to retrieve" if the Uni exists
        // but they aren't allowed to view it. In any case, bounce them back to /unis and show an error message.
        navigate('/uni')
        safeNotify.errorWithInput('Could not load Uni: ${uniName}. Encountered the following error: ${error}', {
          uniName,
          error: data.errors?.[0]?.message,
        })
        return
      }

      const getUni = {
        ...data.getUni,
        schemaNamespace: 'Self',
      }

      // Fix for dynamodb sentinel value (replaces empty string in db) leaking to frontend
      if (getUni.alias === '\u0000' || getUni.alias === '\x00') {
        getUni.alias = null
      }

      return {
        ...data,
        getUni,
      }
    }, // Similar to useListUnis, make sure we've finished initializing selected role before firing request.
    enabled: Boolean(client) && Boolean(selectedRoleName) && Boolean(uniName),
    staleTime: 1000 * 60 * 5, // 5 minutes
    // Only one caller should enable polling to avoid duplicate requests from multiple observers.
    // Enable via useGetUni({ polling: true }) in a single layout component.
    refetchInterval: polling
      ? (query) => {
          const status = query.state.data?.getUni?.status
          if (status === StatusEnum.Evolving) return 3 * 1000
          if (status && status !== StatusEnum.Running) return 30 * 1000
          return false
        }
      : false,
    refetchIntervalInBackground: polling,
  })

  // if (response.isSuccess) {
  //   setUniStatus(response.data?.getUni?.status)
  // }

  return response
}
